import dayjs from "dayjs";
import minMax from 'dayjs/plugin/minMax.js'
import weekOfYear from "dayjs/plugin/weekOfYear";

dayjs.extend(minMax);
dayjs.extend(weekOfYear);

export type _Date = dayjs.Dayjs | Date | string

export function sleep(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

function getNumberOfDaysInMonth(year: string, month: string) {
    return dayjs(`${year}-${month}-01`).daysInMonth();
}

function getWeekday(date: _Date) {
    return dayjs(date).day();
}

type days = {
    date: string,
    dayOfMonth: number,
    isCurrentMonth: Boolean
}

export function createDaysForCurrentMonth(year: string, month: string): days[] {
    return [...Array(getNumberOfDaysInMonth(year, month))].map((day, index) => {
        return {
            date: dayjs(`${year}-${month}-${index + 1}`).format("YYYY-MM-DD"),
            dayOfMonth: index + 1,
            isCurrentMonth: true
        };
    });
}

export function createDaysForPreviousMonth(currentMonthDays: days[], year: string, month: string) {
    const firstDayOfTheMonthWeekday = getWeekday(currentMonthDays[0].date);

    const previousMonth = dayjs(`${year}-${month}-01`).subtract(1, "month");

    // Cover first day of the month being sunday (firstDayOfTheMonthWeekday === 0)
    const visibleNumberOfDaysFromPreviousMonth = firstDayOfTheMonthWeekday
        ? firstDayOfTheMonthWeekday - 1
        : 6;

    const previousMonthLastMondayDayOfMonth = dayjs(currentMonthDays[0].date)
        .subtract(visibleNumberOfDaysFromPreviousMonth, "day")
        .date();

    return [...Array(visibleNumberOfDaysFromPreviousMonth)].map((day, index) => {
        return {
            date: dayjs(
                `${previousMonth.year()}-${previousMonth.month() + 1}-${previousMonthLastMondayDayOfMonth + index
                }`
            ).format("YYYY-MM-DD"),
            dayOfMonth: previousMonthLastMondayDayOfMonth + index,
            isCurrentMonth: false
        };
    });
}

export function createDaysForNextMonth(currentMonthDays: days[], year: string, month: string) {
    const lastDayOfTheMonthWeekday = getWeekday(
        `${year}-${month}-${currentMonthDays.length}`
    );

    const nextMonth = dayjs(`${year}-${month}-01`).add(1, "month");

    const visibleNumberOfDaysFromNextMonth = lastDayOfTheMonthWeekday
        ? 7 - lastDayOfTheMonthWeekday
        : lastDayOfTheMonthWeekday;

    return [...Array(visibleNumberOfDaysFromNextMonth)].map((day, index) => {
        return {
            date: dayjs(
                `${nextMonth.year()}-${nextMonth.month() + 1}-${index + 1}`
            ).format("YYYY-MM-DD"),
            dayOfMonth: index + 1,
            isCurrentMonth: false
        };
    });
}


export function minuteInterval(startDate: _Date, endDate: _Date, minutes: number = 15) {
    let start = dayjs(startDate)
    let end = dayjs(endDate)

    // round starting minutes up to nearest 15 (12 --> 15, 17 --> 30)
    // note that 59 will round up to 60, and moment.js handles that correctly
    start = roundTime(start)

    let result = [];

    let current = dayjs(start);

    while (current <= end) {
        result.push(current.format('YYYY-MM-DD HH:mm'));
        current = current.add(15, 'minutes');
    }

    return result;
}

export function roundTime(date: _Date, minutes: number = 15) {
    return dayjs(date).minute(Math.ceil(dayjs(date).minute() / minutes) * minutes);

}

export function findDifference(list1: [_Date, _Date][], list2: [_Date, _Date][]) {
    const result: { [key: string]: boolean } = {};

    // Loop through each range in list1
    while (list1.length > 0) {
        const range1 = list1.pop()
        const r1 = range1!.map(r => dayjs(r));
        const [start1, end1] = r1;

        // Check for overlaps or complete containment in list2
        let foundOverlap = false;
        let splitted = false;
        for (const range2 of list2) {
            const [start2, end2] = range2.map(r => dayjs(r));

            // Check if range1 completely contains range2
            if (start1 < start2 && end1 > end2) {
                splitted = true
                // Add non-overlapping part before range2
                if (start1 < start2) {
                    list1.push([start1, start2]);
                }

                // Add non-overlapping part after range2
                if (end1 > end2) {
                    list1.push([end2, end1]);
                }
                continue; // No need to check further overlaps for a splitted range1
            }

            // Check if there's an overlap
            if (start1 < end2 && end1 > start2) {
                foundOverlap = true;

                // Add non-overlapping parts of range1
                if (start1 < start2) {
                    list1.push([start1, dayjs.min(end1, start2)!]);
                }
                if (end1 > end2) {
                    list1.push([dayjs.min(start1, end2)!, end1]);
                }
                continue; // No need to check further overlaps for this range1
            }
        }

        // If no overlap or splits found in list2, add the entire range1 to result
        if (!foundOverlap && !splitted) {
            result[r1.map(r => r.toISOString()).join(',')] = true;
        }
    }

    // return result
    return Object.keys(result).map(r => r.split(','))//.map(e => dayjs(e)))
}
